import { TextField, Typography } from '@freighthub/silhouette-core'
import { FC, ReactNode } from 'react'
import { useController } from 'react-hook-form'
import { useI18n } from 'utils/localisation'
import { z } from 'zod'

import {
  FormFieldsBehaviour,
  FormSchemaBehaviour,
  HeaderBehaviour,
  MQFields,
  ServiceBehaviour,
  SummaryBehaviour,
} from './MQFormContentProcessor'
import { MQService } from './MQServices'

const MQDangerousGoodsHeader: FC = () => {
  const { translate } = useI18n()

  return <>{translate('manual_quote-dangerous_goods-header')}</>
}
export class MQDangerousGoodsHeaderBehaviour implements HeaderBehaviour {
  content = (<MQDangerousGoodsHeader />)
}

const MIN_CHARS = 20

type DangerousGoodsContentProps = {
  salesManagerEmail: string
}
export const DangerousGoodsSubContent: FC<DangerousGoodsContentProps> = ({ salesManagerEmail }) => {
  const {
    field: { value, onChange },
    formState: { errors },
  } = useController<MQFields, 'additionalInfo'>({
    name: 'additionalInfo',
  })
  const { translate } = useI18n()

  const textLength = value?.length ?? 0
  const remainingChars = MIN_CHARS - textLength
  const minLengthHelperText = `${translate('manual_quote-customer_note-min')} ${
    remainingChars > 0 ? remainingChars : 0
  } ${translate('manual_quote-customer_note-characters')}`

  return (
    <>
      <Typography variant="body1" paragraph>
        {translate('manual_quote-dangerous_goods-paragraph_1')}
      </Typography>
      <Typography variant="body1" paragraph>
        <span
          dangerouslySetInnerHTML={{
            __html: translate('manual_quote-dangerous_goods-paragraph_3', { salesManagerEmail }),
          }}
        />
      </Typography>
      <Typography variant="body1" paragraph>
        {translate('manual_quote-dangerous_goods-paragraph_2')}
      </Typography>
      <TextField
        label={translate('manual_quote-dangerous_goods-additional_details')}
        onChange={onChange}
        fullWidth
        multiline
        required
        value={value}
        placeholder={translate('manual_quote-dangerous_goods-additional_details-placeholder')}
        error={!!errors.additionalInfo}
        helperText={errors.additionalInfo?.message ?? minLengthHelperText}
      />
    </>
  )
}
export class MQDangerousGoodsSummaryBehaviour implements SummaryBehaviour {
  content: ReactNode
  type = 'dangerous-goods-summary'
  constructor(salesManagerEmail: string) {
    this.content = <DangerousGoodsSubContent salesManagerEmail={salesManagerEmail} />
  }
}

const MQDangerousGoodsServicesContent: FC = () => {
  const { translate } = useI18n()
  return (
    <MQService
      title={translate('common-dangerous_or_special_goods')}
      value={translate('manual_quote-dangerous_or_special_goods-helper')}
    />
  )
}
export class MQDangerousGoodsServicesBehaviour implements ServiceBehaviour {
  type = 'dangerous-goods-service'
  content = (<MQDangerousGoodsServicesContent />)
}

export class MQDangerousGoodsFormSchemaBehaviour implements FormSchemaBehaviour {
  schema = z.object({
    additionalInfo: z.string().min(MIN_CHARS),
  })
}

export class MQDangerousGoodsFormFieldsBehaviour implements FormFieldsBehaviour {
  content = null
}
